import { Injectable } from '@angular/core';
import {
  NetworkUtilsService,
  URL_DICT,
} from 'projects/app-core/src/service/network-utils.service';
import {
  EmailAuditLogEntry,
  UserJourneyResp,
} from '../models/user-profile/UserProfileSearchResponse';
import { PolicyBookingTrace, PolicyEvaluateWinningRule } from '../models/user-profile/UserProfileSearchResponse';
import {
  AnyoWhatsAppTemplateIds,
  AnyoWhatsAppTemplateURLParams,
  PlaceHolders,
} from '../models/AnyoWhatsappTemplate';
import { GallaBoxTemplateComponent } from '../models/GallaboxTemplateResponse';

export interface UserJourneyRequest {
  emailOrCode: string;
}

export interface AdminWhatsappSendReq {
  userId: string;
  templateId: AnyoWhatsAppTemplateIds;
  placeHolderData?: Record<PlaceHolders, string>;
  dynamicId?: string;
  urlParams?: Record<AnyoWhatsAppTemplateURLParams, string>;
}

export interface GallaboxTemplatesResponse {
  anyoTemplateId: AnyoWhatsAppTemplateIds;
  variables: PlaceHolders[];
  components: GallaBoxTemplateComponent[];
  status: string;
  name: string;
  urlParams: AnyoWhatsAppTemplateURLParams[];
}

// DM on/off control row — one per (userId, therapistId) pair with an existing chat session.
// expertToExpertChat pairs are excluded by the backend before this ever reaches the client.
export interface ExpertChatRow {
  therapistId: string;
  therapistName: string;
  lastMessageTime?: string | number;
  messageCount: number;
  messageUserCount?: number;
  messageTherapistCount?: number;
  /** undefined/true = DM enabled, false = disabled. Never render the raw value directly on a
   * switch — bind `dmEnabled !== false` so an unset row still shows as enabled. */
  dmEnabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  constructor(private api: NetworkUtilsService) {}

  public searchUserProfile(req: UserJourneyRequest) {
    return this.api.post<UserJourneyResp>(URL_DICT.userProfileSearchUrl, req);
  }

  public callToUser(userId: string) {
    return this.api.post(URL_DICT.callToUserUrl, { toUserId: userId });
  }

  public sendWhatsappMessage(req: AdminWhatsappSendReq) {
    return this.api.post(URL_DICT.adminSendWhatsappMessageUrl, req);
  }

  public listGallaboxWhatsappTemplate() {
    return this.api.get<GallaboxTemplatesResponse[]>(
      URL_DICT.adminWhatsappTemplatelistUrl,
    );
  }

  public blockUserSessionBooking(
    userId: string,
    sessionBookingBlockedTill: string,
  ) {
    return this.api.post(URL_DICT.blockUserSessionBookingUrl, {
      userId,
      sessionBookingBlockedTill,
    });
  }
  public unblockUserSessionBooking(userId: string) {
    return this.api.post(URL_DICT.unBlockUserSessionBookingUrl, { userId });
  }

  public updateUserProfile(
    userId: string,
    displayName: string,
    phoneNumber: string,
  ) {
    return this.api.post(URL_DICT.userUpdateUserProfileUrl + userId, {
      name: displayName,
      phoneNumber: phoneNumber,
    });
  }

  public updateUserEmail(userId: string, email: string) {
    return this.api.post(URL_DICT.userUpdateUserEmailUrl + userId, {
      email: email,
    });
  }

  public getUserEmailAuditLog(userId: string) {
    return this.api.get<EmailAuditLogEntry[]>(
      URL_DICT.userEmailAuditLogUrl + userId,
    );
  }

  public fixMemberPassRedemption(userId: string) {
    return this.api.post<{
      success: boolean;
      message: string;
      corporateCode: string;
    }>(URL_DICT.fixMemberPassRedemptionUrl, { userId });
  }
  public retryUserLabReportsProcessing(userId: string) {
    return this.api.post(URL_DICT.adminRetryUserLabReportsProcessingUrl, {
      userId,
    });
  }

  public describeRule(ruleId: string) {
    return this.api.post<{ description: string }>(
      URL_DICT.adminPolicyRuleDescribeUrl + ruleId + '/describe',
      {},
    );
  }

  public debugUserPolicy(
    email: string,
    verticalId: string,
    concernId: string,
    therapistId?: string,
  ) {
    return this.api.post<{
      user: { userId: string; email: string; userType: string; partnerId?: string };
      company: { companyId: string; companyName: string; subscriptionStart?: string; subscriptionEnd?: string };
      facts: Record<string, unknown>;
      rules: {
        ruleId: string;
        ruleName: string;
        priority: number;
        isGlobal: boolean;
        matched: boolean;
        outputs?: Record<string, unknown>;
        conditions?: unknown;
        scope?: unknown;
      }[];
      finalOutputs: PolicyBookingTrace['outputs'];
      winnersByField: Record<string, PolicyEvaluateWinningRule | null>;
    }>(URL_DICT.adminPolicyDebugUserUrl, {
      email,
      verticalId,
      concernId,
      ...(therapistId && { therapistId }),
    });
  }

  public getExpertChats(userId: string) {
    return this.api.get<ExpertChatRow[]>(
      URL_DICT.adminUserExpertChatsBaseUrl + userId + '/expert-chats',
    );
  }

  public toggleExpertChatDm(userId: string, therapistId: string, enabled: boolean) {
    return this.api.post(
      URL_DICT.adminUserExpertChatsBaseUrl + userId + '/expert-chats/' + therapistId + '/toggle',
      { enabled },
    );
  }
}
